import React, { useState } from "react";
import { getToolActivityStatusText, type ToolTranscriptActivity } from "./mcp-chat-tool-transcript";
import { CheckIcon, ErrorIcon } from "./mcp-tool-activity";
import { isReadOnlyTool } from "./mcp-tool-descriptions";

interface McpToolCallAccordionProps {
  activities: ToolTranscriptActivity[];
  label?: string;
}

const WrenchIcon: React.FC = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
  </svg>
);

const ChevronIcon: React.FC<{ expanded: boolean }> = ({ expanded }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`mcp-tool-accordion__chevron${expanded ? " mcp-tool-accordion__chevron--open" : ""}`}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const ClockIcon: React.FC = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const ListIcon: React.FC = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="8" y1="6" x2="21" y2="6" />
    <line x1="8" y1="12" x2="21" y2="12" />
    <line x1="8" y1="18" x2="21" y2="18" />
    <line x1="3" y1="6" x2="3.01" y2="6" />
    <line x1="3" y1="12" x2="3.01" y2="12" />
    <line x1="3" y1="18" x2="3.01" y2="18" />
  </svg>
);

function formatDuration(ms: number): string {
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.round(ms)}ms`;
}

function getActivityColor(activity: ToolTranscriptActivity): "read" | "write" | "error" {
  if (activity.status === "error") return "error";
  if (!activity.isMutating && isReadOnlyTool(activity.toolName)) return "read";
  return "write";
}

const TimelineView: React.FC<{ activities: ToolTranscriptActivity[] }> = ({ activities }) => {
  const withTiming = activities.filter((a) => a.startTime != null && a.duration != null);
  if (withTiming.length === 0) {
    return <div className="mcp-tool-timeline__empty">No timing data</div>;
  }

  const minStart = Math.min(...withTiming.map((a) => a.startTime!));
  const maxEnd = Math.max(...withTiming.map((a) => a.startTime! + a.duration!));
  const totalSpan = maxEnd - minStart || 1;

  return (
    <div className="mcp-tool-timeline">
      {activities.map((a) => {
        const hasTiming = a.startTime != null && a.duration != null;
        const leftPct = hasTiming ? ((a.startTime! - minStart) / totalSpan) * 100 : 0;
        const widthPct = hasTiming ? Math.max((a.duration! / totalSpan) * 100, 1) : 0;
        const color = getActivityColor(a);

        return (
          <div key={a.requestId} className="mcp-tool-timeline__row">
            <span className="mcp-tool-timeline__label" title={a.description}>
              {a.description}
            </span>
            <span className="mcp-tool-timeline__track">
              {hasTiming && (
                <span
                  className={`mcp-tool-timeline__bar mcp-tool-timeline__bar--${color}`}
                  style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
                />
              )}
            </span>
            <span className="mcp-tool-timeline__duration">{hasTiming ? formatDuration(a.duration!) : "—"}</span>
          </div>
        );
      })}
    </div>
  );
};

const AccordionItem: React.FC<{ activity: ToolTranscriptActivity }> = ({ activity }) => {
  const isError = activity.status === "error";
  const readOnly = !activity.isMutating && isReadOnlyTool(activity.toolName);
  const statusText = getToolActivityStatusText(activity);

  const itemClass = isError
    ? "mcp-tool-accordion__item--error"
    : readOnly
      ? "mcp-tool-accordion__item--read"
      : "mcp-tool-accordion__item--write";

  return (
    <div className={`mcp-tool-accordion__item ${itemClass}`}>
      <span className="mcp-tool-accordion__item-icon">{isError ? <ErrorIcon /> : <CheckIcon />}</span>
      <div className="mcp-tool-accordion__item-copy">
        <span className="mcp-tool-accordion__item-text">{activity.description}</span>
        <span className="mcp-tool-accordion__item-detail">{statusText}</span>
      </div>
      {activity.duration != null && (
        <span className="mcp-tool-accordion__item-duration">{formatDuration(activity.duration)}</span>
      )}
    </div>
  );
};

export const McpToolCallAccordion: React.FC<McpToolCallAccordionProps> = ({ activities, label }) => {
  const [expanded, setExpanded] = useState(false);
  const [viewMode, setViewMode] = useState<"list" | "timeline">("list");
  const errorCount = activities.filter((a) => a.status === "error").length;
  const hasTimingData = activities.some((a) => a.startTime != null && a.duration != null);

  return (
    <div className="mcp-tool-accordion">
      <button
        className="mcp-tool-accordion__header"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
      >
        <WrenchIcon />
        <span className="mcp-tool-accordion__label">
          {label || `${activities.length} tool call${activities.length !== 1 ? "s" : ""}`}
        </span>
        {errorCount > 0 && (
          <span className="mcp-tool-accordion__error-badge">
            {errorCount} error{errorCount !== 1 ? "s" : ""}
          </span>
        )}
        {hasTimingData && expanded && (
          <button
            className="mcp-tool-accordion__toggle"
            onClick={(e) => {
              e.stopPropagation();
              setViewMode((prev) => (prev === "list" ? "timeline" : "list"));
            }}
            title={viewMode === "list" ? "Timeline view" : "List view"}
          >
            {viewMode === "list" ? <ClockIcon /> : <ListIcon />}
          </button>
        )}
        <ChevronIcon expanded={expanded} />
      </button>
      <div className={`mcp-tool-accordion__body${expanded ? " mcp-tool-accordion__body--open" : ""}`}>
        <div className="mcp-tool-accordion__body-inner">
          {viewMode === "timeline" && hasTimingData ? (
            <TimelineView activities={activities} />
          ) : (
            activities.map((a) => <AccordionItem key={a.requestId} activity={a} />)
          )}
        </div>
      </div>
    </div>
  );
};
